import * as qs from 'qs';
import { getJimDoge, getBunchBox, getGtmDataLayer, getDwh, isJapaneseLanguage } from './helpers';
import {
  BUNCHBOX_ASSETS_ENDPOINT,
  ANALYTICS_EVENT_CATEGORY,
  GTM_UA_EVENT_NAME,
  BUNCHBOX_ASSETS_ENDPOINT_JAPAN,
} from './constants';
import getConfig from '../config';
import { EventPayload } from '@jimdo/frontend-tracking';
import { datadogRum } from '@jimdo/monitoring';

const loadIframe = require('load-iframe');

const config = getConfig();

const eventCategory = ANALYTICS_EVENT_CATEGORY;

export const loadTrackingScript = (src: string) =>
  document.head.appendChild(
    Object.assign(document.createElement('script'), {
      src: src,
      async: true,
    })
  );

export const setWebsiteCreatedCookie = (name: string) => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackUserWithCookie: ', name);
  }

  const date = new Date();
  const now = date.getTime();
  date.setTime(now + 365 * 24 * 60 * 60 * 1000);
  const expires = date.toUTCString();
  document.cookie = `${name}=${now};path=/; domain=.jimdo.com;expires=${expires}`;
};

export const trackTiming = (name: string, value: number, label?: string): void => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackTiming: ', name, `${(value / 1000).toFixed(2)}s`, label || '');
  }

  const jDoge = getJimDoge();
  const gtmDataLayer = getGtmDataLayer();

  if (jDoge) {
    jDoge.push(['timing', eventCategory, name, value, label]);
  }

  if (gtmDataLayer) {
    gtmDataLayer.push({
      event: 'timing',
      timingCategory: eventCategory,
      timingVar: name,
      timingValue: value,
      timingLabel: label,
    });
  }
};

export const trackError = (label: string, action?: string): void => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.warn('trackError: ', label);
  }

  const jDoge = getJimDoge();
  const gtmDataLayer = getGtmDataLayer();

  if (jDoge) {
    jDoge.push(['event', 'the-signup-flow', label, action]);
  }

  if (gtmDataLayer) {
    gtmDataLayer.push({
      event: GTM_UA_EVENT_NAME,
      eventCategory: 'the-signup-flow',
      eventAction: action,
      eventLabel: label,
    });
  }
};

export const loadTrackingIframe = (
  trackingEndpoint: string,
  locale: string,
  trial?: string
): Promise<{}> => {
  const query = qs.stringify({ locale, trial });
  return new Promise((res) => {
    loadIframe(`${trackingEndpoint}?${query}`, res);
  });
};

export const initBunchbox = (): void => {
  if (config.jimdoEnvironment !== 'prod' || typeof window === 'undefined') {
    return undefined;
  }

  window._bb = window._bb || [];

  const bunchBoxScriptSrc = isJapaneseLanguage(getConfig().language)
    ? BUNCHBOX_ASSETS_ENDPOINT_JAPAN
    : BUNCHBOX_ASSETS_ENDPOINT;

  loadTrackingScript(bunchBoxScriptSrc);

  ['_bb', '_bb_t'].forEach((cookie: string) => {
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
  });

  window._bb.push(['attribute', 'language', config.language]);
  window._bb.push(['cookie domain', 'jimdo.com'], ['auto evaluation', false]);
};

export const trackPageview = (pathname: string): void => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackPageview: ', pathname);
  }

  const jDoge = getJimDoge();

  if (jDoge) {
    if (pathname) {
      jDoge.push(['page', pathname]);
      return;
    }
    jDoge.push(['page']);
  }
};

export const trackEvent = (
  actionType: string,
  additional?: {},
  nonInteractive: boolean = true
): void => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackEvent: ', actionType, additional);
  }

  const jDoge = getJimDoge();
  const gtmDataLayer = getGtmDataLayer();
  const additionalData = typeof additional === 'string' ? additional : JSON.stringify(additional);

  if (jDoge) {
    jDoge.push(['event', eventCategory, actionType, additionalData, null, nonInteractive]);
  }

  if (gtmDataLayer) {
    gtmDataLayer.push({
      event: GTM_UA_EVENT_NAME,
      eventCategory,
      eventAction: actionType,
      eventLabel: additionalData,
    });
  }
};

export const trackGtmEvent = (event: string, eventData?: {}): void => {
  const gtmDataLayer = getGtmDataLayer();

  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackGtmEvent: ', eventData);
  }

  if (gtmDataLayer) {
    gtmDataLayer.push({ event, ...eventData });
  }
};

export const trackErrorOnAction = (actionType: string, additional?: {}): void => {
  const additionalData = typeof additional === 'string' ? additional : JSON.stringify(additional);
  trackError(actionType, additionalData);
};

export type TrackingAction =
  | 'action'
  | 'category-view'
  | 'checkout-config'
  | 'creation-success'
  | 'domain-view'
  | 'element'
  | 'experiment'
  | 'industry-view'
  | 'pricing-view'
  | 'product-choice-view'
  | 'root'
  | 'signup-flow-wizardify-redesign-test'
  | 'template-view'
  | 'experiment'
  | 'user-event';

export type TrackingLabel =
  | 'change'
  | 'click'
  | 'continue'
  | 'existed'
  | 'extract'
  | 'fail'
  | 'forward'
  | 'generate'
  | 'increase'
  | 'invalid'
  | 'load'
  | 'mount'
  | 'pass'
  | 'ready'
  | 'request'
  | 'select'
  | 'set'
  | 'show'
  | 'skip'
  | 'success'
  | 'test'
  | 'transform'
  | 'unload'
  | 'unmount'
  | 'validate'
  | 'allocation';

export type TrackedSignupActionName =
  | 'active-domain-type'
  | 'affiliate-id'
  | 'back-button'
  | 'amount-templates'
  | 'category'
  | 'category-view'
  | 'checkout'
  | 'checkout-config'
  | 'domain'
  | 'domain-validation'
  | 'domain-value'
  | 'free-domain-hint'
  | 'general-request'
  | 'industry'
  | 'industry-view'
  | 'login-link'
  | 'mobile-settings'
  | 'other'
  | 'package'
  | 'password'
  | 'persisted-state'
  | 'pricing-view'
  | 'product'
  | 'product-choice-view'
  | 'product-domain-hint'
  | 'product-tld'
  | 'subtype'
  | 'template-view'
  | 'three-month-discount-badge'
  | 'tld-value'
  | 'transfer-domain-hint'
  | 'trial-type'
  | 'user-id-token'
  | 'user-account-id-token' // TODO cleanup GRW-1410 GRW-1416
  | 'username-validation'
  | 'username-value'
  | 'website'
  | 'website-creation'
  | 'website-id';

export const trackDWH = (
  action: TrackingAction | TrackedSignupActionName,
  label: TrackingLabel,
  payload: EventPayload
): void => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackDWH: ', action, label, payload);
  }

  datadogRum.addAction(`signup-flow.${action}.${label}`, payload);

  const track = getDwh();
  track({
    category: 'signup-flow',
    action,
    label,
    payload,
  });
};

export const trackToBunchBox = (...data: {}[]): void => {
  if (config.jimdoEnvironment !== 'prod') {
    // eslint-disable-next-line no-console
    console.log('trackToBunchBox: ', data);
  }

  const bb = getBunchBox();
  if (bb) {
    bb.push(data);
  }
};

export const trackInteractiveTime = (now: number) => {
  const performance =
    window.performance || window.webkitPerformance || window.msPerformance || window.mozPerformance;
  if (!performance || !performance.timing || !performance.navigation) {
    return;
  }
  if (performance.navigation.type !== performance.navigation.TYPE_NAVIGATE) {
    return;
  }
  const delta = now - performance.timing.fetchStart;
  if (delta > 0 && delta < 3600) {
    trackTiming('time to app interactive', delta);
  }
};
